import { useState } from 'react';
import { Link } from 'react-router';
import type { Program, ProgramReport, ProgramReportCreate, ReportConclusion } from '../api/usePrograms.ts';
import { Knapp } from '../innboks/Detalj.tsx';
import { Dialog } from '../skall/Dialog.tsx';
import { Om } from '../skall/Om.tsx';
import { Ikon } from '../status/Ikon.tsx';
import { Tallrekke } from './Tallrekke.tsx';
import type { Tall as TallRad } from './tilstand.ts';

/** DD-16/DD-35: under dette antallet svar finnes ingen prosent noe sted. Speiler serverens gulv. */
export const PROSENTGULV = 30;

/** DD-35, ordrett — samme setning som testeren får i takken. */
export const IKKE_NOK =
  'Ikke nok data ennå — det betyr ikke at funksjonen er dårlig, bare at vi vet for lite.';

const KONKLUSJONER: Array<[ReportConclusion, string, string]> = [
  ['oppfylt', 'checkmark-circle-outline', 'Oppfylt'],
  ['delvis', 'pause-circle-outline', 'Delvis'],
  ['ikke', 'close-circle-outline', 'Ikke oppfylt'],
];

/**
 * 4d — avslutt med rapport. Konklusjonen er mot SUKSESSKRITERIET (DD-17), aldri «folk likte
 * det»: kriteriet står øverst i skjemaet, så valget måles mot noe.
 *
 * To rader er obligatoriske (DD-35): «Åpne blokkerende» kan være tom liste — «ingen» er et
 * svar, «ikke spurt» er ikke — og «Begrensninger i data» kan ikke være tom under terskelen.
 * Under 30 svar viser skjemaet setningen testeren får i stedet for tall, så produkteier ser
 * nøyaktig hva som sendes ut.
 */
export function Avslutt({
  program,
  svar,
  opptatt,
  onAvslutt,
  onAvbryt,
}: {
  program: Program;
  /** Antall svar på oppfølginger — terskelen måles på dette (DD-16). */
  svar: number;
  opptatt: boolean;
  onAvslutt: (body: ProgramReportCreate) => void;
  onAvbryt: () => void;
}) {
  // P4 (H3.4): ingen forhåndsvalgt konklusjon, for alle program. Rapportens konklusjon kan bli
  // forslaget til en hypoteses utfall, og et forhåndsvalg her ville gjort H20 til en formalitet.
  const [konklusjon, setKonklusjon] = useState<ReportConclusion | null>(null);
  const [nesteSteg, setNesteSteg] = useState('');
  const [blokkerende, setBlokkerende] = useState<string[]>([]);
  const [nyBlokkerende, setNyBlokkerende] = useState('');
  const [begrensninger, setBegrensninger] = useState('');

  const forFaa = svar < PROSENTGULV;
  const manglerBegrensninger = forFaa && begrensninger.trim() === '';
  const grunn = konklusjon === null ? 'Velg konklusjonen mot suksesskriteriet først' : nesteSteg.trim() === '' ?'Neste steg må stå — rapporten er hva dere gjør nå' : manglerBegrensninger ? `Under ${PROSENTGULV} svar må «Begrensninger i data» si hvorfor det ikke finnes prosent` : opptatt ? 'Avslutter …' : null;

  const leggTil = () => {
    const tekst = nyBlokkerende.trim();
    if (tekst === '' || blokkerende.length >= 20) return;
    setBlokkerende((x) => [...x, tekst]);
    setNyBlokkerende('');
  };

  return (
    <Dialog tittelId="prg-avslutt-h" onLukk={onAvbryt} testid="prg-avslutt-dialog" className="panel-prg-rapport">
        <h3 id="prg-avslutt-h" className="panel-h3">
          Avslutt «{program.name}»
        </h3>
        <p className="panel-etikett">
          Rapporten fryses i det du avslutter og endres aldri. Samme tall går til dere, til systemene deres og til testerens takk — ingen pynting på veien ut.
        </p>

        {program.successCriterion ? (
          <p className="panel-tekst" data-testid="prg-kriterium-vist">
            <Ikon navn="flag-outline" /> Suksesskriterium: {program.successCriterion.text}
            {program.successCriterion.target ? ` · ${program.successCriterion.target}` : ''}
          </p>
        ) : null}

        <fieldset className="panel-felt">
          <legend className="panel-felt-navn">Konklusjon mot suksesskriteriet</legend>
          <div className="panel-prg-konklusjon">
            {KONKLUSJONER.map(([verdi, ikon, navn]) => (
              <label key={verdi} className={`panel-brikke-knapp${konklusjon === verdi ? ' panel-brikke-valgt' : ''}`}>
                <input type="radio" name="konklusjon" value={verdi} checked={konklusjon === verdi} onChange={() => setKonklusjon(verdi)} data-testid={`prg-konklusjon-${verdi}`} />
                <Ikon navn={ikon} /> {navn}
              </label>
            ))}
          </div>
        </fieldset>

        <label className="panel-felt">
          <span className="panel-felt-navn">Neste steg</span>
          <textarea rows={2} maxLength={1000} value={nesteSteg} onChange={(e) => setNesteSteg(e.target.value)} data-testid="prg-neste-steg" placeholder="Hva dere gjør nå — og hva som ikke blir gjort." />
        </label>

        <div className="panel-felt">
          <span className="panel-felt-navn">Åpne blokkerende funn</span>
          <span className="panel-etikett">Obligatorisk rad, men kan være tom: «ingen blokkerende funn» er et svar, «ikke spurt» er det ikke.</span>
          {blokkerende.length > 0 ? (
            <ul className="panel-prg-rader" data-testid="prg-blokkerende">
              {blokkerende.map((b, i) => (
                <li key={b}>
                  <span className="panel-brikke">
                    <Ikon navn="warning-outline" /> {b}
                  </span>
                  <button type="button" className="panel-knapp-liten" onClick={() => setBlokkerende((x) => x.filter((_, j) => j !== i))} aria-label={`Fjern «${b}»`}>
                    Fjern
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <span className="panel-prg-oppgave-tall">
            <input type="text" value={nyBlokkerende} onChange={(e) => setNyBlokkerende(e.target.value)} aria-label="Nytt blokkerende funn" data-testid="prg-blokkerende-nytt" placeholder="Kort setning" />
            <button type="button" className="panel-knapp-sekundaer" onClick={leggTil} data-testid="prg-blokkerende-legg-til">
              Legg til
            </button>
          </span>
        </div>

        <label className="panel-felt">
          <span className="panel-felt-navn">Begrensninger i data</span>
          <textarea
            rows={2}
            maxLength={1000}
            value={begrensninger}
            onChange={(e) => setBegrensninger(e.target.value)}
            aria-invalid={manglerBegrensninger ? 'true' : undefined}
            aria-describedby={forFaa ? 'prg-terskel' : undefined}
            data-testid="prg-begrensninger"
            placeholder="Hva tallene ikke kan si."
          />
          {forFaa ? (
            <span id="prg-terskel" className="panel-grunn" data-testid="prg-terskel">
              <Ikon navn="warning-outline" /> {svar} svar er under {PROSENTGULV}: rapporten får ingen prosent, og testeren ser «{IKKE_NOK}» Da må denne raden si hvorfor.
            </span>
          ) : (
            <span className="panel-etikett">Kan være tom når det er nok svar til prosent.</span>
          )}
        </label>

        <div className="panel-prg-knapper">
          <Knapp label="Behold programmet" kbd="Esc" onClick={onAvbryt} testid="prg-avslutt-nei" />
          <Knapp
            label="Avslutt og frys rapporten"
            primaer
            grunn={grunn}
            onClick={() => konklusjon !== null && onAvslutt({ conclusion: konklusjon, nextSteps: nesteSteg.trim(), openBlocking: blokkerende, dataLimitations: begrensninger.trim() })}
            testid="prg-avslutt-ja"
          />
        </div>
    </Dialog>
  );
}

/**
 * Den frosne rapporten (R2.3, program-konsept v0.1 skjerm 3).
 *
 * Konklusjonsordet er skjermens HELT, på en hevet flate, med neste steg ved siden av — de
 * sju like tellekortene der seks viste 0 er borte (F2). Tallrekka har ett tall med
 * intervall når det finnes; under terskelen står DD-35-setningen ORDRETT der prosenten
 * ville stått, og fordelingen av svar vises uansett antall (DD-16).
 *
 * De to obligatoriske radene (DD-35) er RADER i en definisjonsliste, ikke kort. «Åpne
 * blokkerende» er fritekst (`openBlocking: string[]`) — ingen lenke, ingen saksnummer
 * plattformen slår opp; eier kan skrive nummeret i teksten selv.
 */
export function FrossenRapport({ rapport, fraProgrammet }: { rapport: ProgramReport; /** Saker fra programmet, talt i innboksen — samme tall som funn-linja ellers viser. */ fraProgrammet: { saker: number; kjent: number } }) {
  const p = rapport.payload;
  const konklusjon = KONKLUSJONER.find(([v]) => v === rapport.conclusion);
  const pst = p.percentages;

  const rader: TallRad[] = [
    { key: 'svar', navn: 'Svar på oppfølging', verdi: String(p.responses) },
    ...(pst
      ? [
          {
            key: 'fullfoert',
            navn: 'Fullførte alle oppgavene',
            verdi: `${Math.round(pst.completionRate * 100)} %`,
            tillegg: `± ${Math.round(((pst.completionRateHigh - pst.completionRateLow) / 2) * 100)}`,
            testid: 'prg-prosent',
          },
        ]
      : []),
    { key: 'med', navn: 'Med', verdi: String(p.accepted), dempet: true },
    { key: 'trukket', navn: 'Trukket', verdi: String(p.withdrawn), dempet: true },
  ].filter((r, i) => i === 0 || r.verdi !== '0');

  return (
    <section className="panel-prg-rapport-vist" aria-label="Rapport" data-testid="prg-rapport">
      <div className="panel-prg-konk">
        <div className="panel-prg-konk-ord">
          <span className="panel-etikett">Konklusjon</span>
          {/* Ordet står i sin EGEN node, ikke som tekst ved siden av ikonet: da er den
              målbart skjermens største tekstnode (R2.3s grønt-kriterium). */}
          <span className="panel-prg-konk-helt" data-testid="prg-konklusjon-helt">
            {konklusjon ? <Ikon navn={konklusjon[1]} /> : null}
            <span className="panel-prg-konk-ordet">{konklusjon ? konklusjon[2] : rapport.conclusion}</span>
          </span>
        </div>
        <div className="panel-prg-konk-neste">
          <span className="panel-etikett">Neste steg</span>
          <p className="panel-prg-neste-tekst" data-testid="prg-neste-steg-vist">
            {rapport.nextSteps}
          </p>
        </div>
      </div>

      <Tallrekke rader={rader} testid="prg-rapport-tall" om={false} />
      {pst ? null : (
        <p className="panel-prg-ikkenok" data-testid="prg-ikke-nok">
          {IKKE_NOK}
        </p>
      )}

      {/* DD-16: fordelingen er TELLINGER og vises uansett antall — én linje, aldri gjennomsnitt. */}
      {p.responses > 0 ? (
        <p className="panel-prg-svarlinje" data-testid="prg-svarfordeling">
          Svarene: <b>{p.responseOutcomes.fungerte}</b> fungerte · {p.responseOutcomes.delvis} delvis · {p.responseOutcomes.ikke} ikke
        </p>
      ) : null}

      <dl className="panel-prg-raderdl" data-testid="prg-rapport-rader">
        <dt>Åpne blokkerende</dt>
        <dd data-testid="prg-blokkerende-vist">{rapport.openBlocking.length > 0 ? rapport.openBlocking.join(' · ') : 'Ingen'}</dd>
        <dt>Begrensninger i data</dt>
        <dd>{rapport.dataLimitations.trim() || 'Ingen'}</dd>
        <dt>Fra programmet</dt>
        <dd>
          <Link to="/innboks" className="panel-lenke" data-testid="prg-fra-programmet">
            {fraProgrammet.saker} saker · {fraProgrammet.kjent} traff kjent feil <Ikon navn="chevron-forward-outline" />
          </Link>
        </dd>
      </dl>

      {p.taskOutcomes.length > 0 ? (
        <ol className="panel-prg-utfall" aria-label="Fullført per oppgave" data-testid="prg-oppgaveutfall">
          {p.taskOutcomes.map((o, i) => (
            <li key={o.taskId}>
              <span className="panel-etikett">{i + 1}</span>
              <span className="panel-prg-utfall-navn">{o.title}</span>
              <span className="panel-prg-utfall-tall">
                <b>{o.fungerte}</b> fungerte <span>{o.delvis} delvis</span> <span>{o.ikke} ikke</span>
              </span>
            </li>
          ))}
        </ol>
      ) : null}

      <Om tittel="rapporten" testid="prg-om-rapporten">
        <p>Rapporten ble fryst {new Date(rapport.frozenAt).toLocaleDateString('nb-NO', { day: 'numeric', month: 'long' })} og endres aldri.</p>
        <p>Samme tall går til dere, til systemene deres og til testerens takk — ingen pynting på veien ut.</p>
        <p>Under {PROSENTGULV} svar finnes ingen prosent noe sted: tellingene er ekte uansett antall, men andeler er de ikke.</p>
      </Om>
    </section>
  );
}
